import {
  Flex,
  useBreakpointValue,
  Box,
  Collapse,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';
import { useQuery } from '@tanstack/react-query';
import { ReactElement } from 'react';
import { RequisitionDetailsPopover } from '@/pages/requisitions/associate-req/components/RequisitionDetailsPopover';
import { useKFlags } from '@/hooks/use-k-flags';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import {
  constructEquityLabel,
  formatOfferSalary,
  formatSalaryRange,
  isSalaryAboveRange,
} from '@/utils/offerUtils';
import { AboveRangeTag } from './AboveRangeTag';
import { ChevronDown } from '@kula-ai/icons/outline/ChevronDown';
import { ChevronUp } from '@kula-ai/icons/outline/ChevronUp';

interface FieldConfig {
  variable: string;
  id: string;
  label: string;
  value: any;
  widthRatio: number;
  customRender?: (value: string) => ReactElement;
}

const findFieldByVariable = (staticFields, variable) => {
  if (staticFields?.length === 0) return null;
  for (const section of staticFields) {
    const found = section?.fields?.find((f) => f?.variable === variable);
    if (found) return found;
  }
  return null;
};

export const OfferDetails = ({
  offer,
  isPreviewMobile,
}: {
  offer: any;
  isPreviewMobile?: boolean;
}) => {
  const isDesktop = useBreakpointValue({ base: false, md: false, lg: true });
  const isMobile = !isDesktop;
  const { isOpen: isDetailsOpen, onToggle: toggleDetails } = useDisclosure();

  const {
    salary,
    salary_currency,
    start_date,
    job_title,
    ats_equity_award,
    requisition,
    today_date,
    expiration_date,
    additional_compensation,
    additional_compensation_currency,
    salary_frequency,
  } = offer || {};

  // Extract equity details
  const {
    equity_type,
    award_type,
    amount: equityAmount,
    currency: equityCurrency,
    cliff,
    unit_of_cliff,
    cycle_after_cliff,
    unit_of_cycle_after_cliff,
    vesting_period,
    unit_of_vesting_period,
  } = ats_equity_award || {};
  const { feReq } = useKFlags();
  const { data: ats_form_template = {}, isLoading: isFormFieldsLoading } =
    useQuery({
      queryKey: ['offer/custom_fields', 'offer'],
      queryFn: async () => {
        const response = await fetchSelectOptions({
          endPoint: 'ats_custom_fields/form_fields?subject_type=ats_offer',
          method: 'get',
        });
        return response?.ats_form_template || {};
      },
      staleTime: 0,
    });

  const { static_fields = [] } = ats_form_template || {};

  const equityLabel = constructEquityLabel(ats_equity_award);
  const currUser = useSelector(selectCurrUser);
  const userHasReqEnabled = currUser?.requisition?.enabled && feReq;
  const requisitionLabel = requisition?.code ?? '-';

  // Check if offer salary exceeds requisition salary range
  const { minimum_salary, maximum_salary } = requisition || {};
  const reqCurrency = requisition?.salary_currency?.currency || 'USD';
  const shouldShowAboveRange =
    userHasReqEnabled && isSalaryAboveRange(salary, maximum_salary);

  const salaryDisplayValue = formatOfferSalary(salary, salary_currency);

  const salaryRangeText =
    minimum_salary !== undefined && maximum_salary !== undefined
      ? formatSalaryRange(minimum_salary, maximum_salary, reqCurrency)
      : '';

  const fieldConfigs: FieldConfig[] = [
    {
      variable: 'salary',
      id: 'salary',
      label: 'Salary',
      value: salaryDisplayValue,
      widthRatio: 1,
      customRender:
        shouldShowAboveRange && salaryRangeText
          ? () => (
              <Flex alignItems="center" gap="6px" flexWrap="wrap">
                <AboveRangeTag salaryRangeText={salaryRangeText} />
              </Flex>
            )
          : undefined,
    },
    // To use the format from the profile settings
    {
      variable: 'start_date',
      id: 'startDate',
      label: 'Start date',
      value: start_date,
      widthRatio: 1,
    },
    {
      variable: 'job_title',
      id: 'Role',
      label: 'Job title',
      value: job_title,
      widthRatio: 1,
    },
    {
      variable: 'equity_amount',
      id: 'Equity',
      label: 'Equity',
      value: equityLabel,
      widthRatio: 2,
    },
    userHasReqEnabled && {
      variable: 'requisition_label',
      id: 'Req_id',
      label: 'Req ID',
      value: requisitionLabel,
      widthRatio: 1,
      customRender: (value: string) => (
        <RequisitionDetailsPopover
          value={value}
          requisition={requisition}
          readOnlyOnMobile={!!isPreviewMobile}
        />
      ),
    },
  ].filter(Boolean);

  const detailsConfig = fieldConfigs.filter(({ variable, value }) => {
    const field = findFieldByVariable(static_fields, variable);
    const hidden = field?.hidden;
    const hasValue = value && value?.trim() !== '-';
    const shouldShow = hasValue || !hidden;
    return shouldShow;
  });

  const totalWidthRatio: number = detailsConfig.reduce(
    (sum: number, { widthRatio }: { widthRatio: number }) => sum + widthRatio,
    0
  );

  if (isFormFieldsLoading || !detailsConfig.length) {
    return <Flex p="12px 18px" bg="#F7F7F7" borderRadius="6px" width="100%" />;
  }

  // Format additional compensation
  const formatAdditionalCompensation = () => {
    if (!additional_compensation) return '-';
    const currencySymbol = additional_compensation_currency?.symbol || '$';
    return `${currencySymbol}${Number(
      additional_compensation
    ).toLocaleString()}`;
  };

  // Format equity display value
  const formatEquityUnits = () => {
    if (!equityAmount) return '-';
    if (equityCurrency) {
      return `${equityCurrency?.symbol || '$'}${Number(
        equityAmount
      ).toLocaleString()}`;
    }
    return equityAmount;
  };

  // Get stock award type label
  const getStockAwardTypeLabel = () => {
    if (!award_type) return '-';
    return award_type === 'money' ? 'Cash value' : 'Units';
  };

  // Get equity type label
  const getEquityTypeLabel = () => {
    if (!equity_type) return '-';
    const typeLabels: Record<string, string> = {
      common_stock: 'Common stock',
      preferred_stock: 'Preferred stock',
      stock_options: 'Stock options',
      rsu: 'RSU',
    };
    return typeLabels[equity_type] || equity_type;
  };

  // Mobile extra details configuration
  const mobileExtraDetails = [
    { label: 'Offer date', value: today_date || '-' },
    { label: 'Start date', value: start_date || '-' },
    { label: 'Salary', value: salaryDisplayValue || '-' },
    { label: 'Offer expiration date', value: expiration_date || '-' },
    {
      label: 'Additional bonus (one-time)',
      value: formatAdditionalCompensation(),
    },
    { label: 'Type of equity', value: getEquityTypeLabel() },
    { label: 'Stock award type', value: getStockAwardTypeLabel() },
    { label: 'Equity units/amount', value: formatEquityUnits() },
    cliff && {
      label: 'Cliff period',
      value: `${cliff} ${unit_of_cliff || 'months'}`,
    },
    vesting_period && {
      label: 'Vesting period',
      value: `${vesting_period} ${unit_of_vesting_period || 'years'}`,
    },
    cycle_after_cliff && {
      label: 'Post-cliff cycle',
      value: `${cycle_after_cliff} ${unit_of_cycle_after_cliff || 'months'}`,
    },
  ].filter(Boolean);

  // Mobile layout: specific order - Row1: Role, Start date | Row2: Salary, Req ID | Row3: Equity
  const getMobileOrderedFields = () => {
    const fieldMap = detailsConfig.reduce((acc, field) => {
      acc[field.variable] = field;
      return acc;
    }, {} as Record<string, FieldConfig>);

    const mobileOrder = [
      fieldMap['job_title'],
      fieldMap['start_date'],
      fieldMap['salary'],
      fieldMap['requisition_label'],
      fieldMap['equity_amount'],
    ].filter(Boolean);

    return mobileOrder;
  };

  const renderField = (
    { id, label, value, widthRatio, customRender, variable }: FieldConfig,
    isMobileLayout: boolean,
    isLastRow?: boolean
  ) => {
    const widthPercent: number = (100 / totalWidthRatio) * widthRatio;
    const isEquity = variable === 'equity_amount';

    return (
      <Flex
        key={id}
        flexDir="column"
        gap="10px"
        width={
          isMobileLayout
            ? isLastRow
              ? '100%'
              : 'calc(50% - 6px)'
            : { base: '100%', md: '100%', lg: `${widthPercent}%` }
        }
      >
        <KText size="sm-semi-bold" color="secondary-700" noOfLines={1}>
          {label}
        </KText>
        <Flex alignItems="center" gap="6px" flexWrap="wrap">
          <KText
            maxW="100%"
            size="md-medium"
            color="secondary-900"
            enableTooltip={isDesktop && isEquity}
            isTruncated={isDesktop && isEquity}
            tooltipText={value}
            style={{
              whiteSpace: isEquity && !isDesktop ? 'normal' : 'nowrap',
              overflow: isEquity && !isDesktop ? 'visible' : 'hidden',
              textOverflow: isEquity && !isDesktop ? 'unset' : 'ellipsis',
            }}
          >
            {value || '-'}
          </KText>
          {customRender && customRender(value)}
        </Flex>
      </Flex>
    );
  };

  const mobileFields = getMobileOrderedFields();

  return (
    <Flex flexDir="column" gap="12px">
      {/* Summary Section */}
      <Flex
        p="12px 18px"
        bg="secondary-25"
        borderRadius="6px"
        width="100%"
        justifyContent="flex-start"
        flexWrap={{ base: 'wrap', md: 'wrap', lg: 'nowrap' }}
        gap="12px"
        maxW="100%"
      >
        {isMobile || isPreviewMobile
          ? // Mobile layout: 2 columns per row, equity full width
            mobileFields.map((field, index) => {
              const isEquityField = field.variable === 'equity_amount';
              return renderField(field, true, isEquityField);
            })
          : // Desktop layout - original code
            detailsConfig?.map(
              ({ id, label, value, widthRatio, customRender, variable }) => {
                const widthPercent: number =
                  (100 / totalWidthRatio) * widthRatio;
                const isEquity = variable === 'equity_amount';

                return (
                  <Flex
                    key={id}
                    flexDir="column"
                    gap="10px"
                    width={{
                      base: '100%',
                      md: '100%',
                      lg: `${widthPercent}%`,
                    }}
                  >
                    <KText
                      size="sm-semi-bold"
                      color="secondary-900"
                      noOfLines={1}
                    >
                      {label}
                    </KText>
                    <Flex alignItems="center" gap="6px" flexWrap="wrap">
                      <KText
                        maxW="100%"
                        size="sm-medium"
                        enableTooltip={isDesktop && isEquity}
                        isTruncated={isDesktop && isEquity}
                        tooltipText={value}
                        style={{
                          whiteSpace:
                            isEquity && !isDesktop ? 'normal' : 'nowrap',
                          overflow:
                            isEquity && !isDesktop ? 'visible' : 'hidden',
                          textOverflow:
                            isEquity && !isDesktop ? 'unset' : 'ellipsis',
                        }}
                      >
                        {value || '-'}
                      </KText>
                      {customRender && customRender(value)}
                    </Flex>
                  </Flex>
                );
              }
            )}
      </Flex>
      {/* Mobile Expandable Offer Details Section
      {(isMobile || isPreviewMobile) && (
        <Box bg="secondary-25" borderRadius="6px">
          <Flex
            alignItems="center"
            justifyContent="space-between"
            cursor="pointer"
            onClick={toggleDetails}
            borderTop="1px solid"
            borderBottom="1px solid"
            borderColor="border-color"
            p="12px 18px"
          >
            <Flex alignItems="center" gap="8px">
              <Icon
                as={isDetailsOpen ? ChevronUp : ChevronDown}
                w="20px"
                h="20px"
                color="secondary-700"
              />
              <KText size="md-semi-bold" color="secondary-900">
                Offer details
              </KText>
            </Flex>
          </Flex>

          <Collapse in={isDetailsOpen} animateOpacity>
            <Flex flexDir="column" gap="16px" px="18px" pb="12px">
              {mobileExtraDetails.map((detail: any, index) => (
                <Flex
                  key={index}
                  flexDir="column"
                  gap="4px"
                  pt={index === 0 ? '22px' : undefined}
                >
                  <KText size="sm-semi-bold" color="secondary-700">
                    {detail.label}
                  </KText>
                  <KText size="sm-medium" color="secondary-900">
                    {detail.value}
                  </KText>
                </Flex>
              ))}
            </Flex>
          </Collapse>
        </Box>
      )} */}
    </Flex>
  );
};
